import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const contactCards = [
  {
    icon: 'ri-phone-fill',
    label: 'Call Us',
    value: '910-242-4567',
    sub: 'Mon – Sat: 9am – 9pm',
    href: 'tel:9102424567',
    accent: 'from-[#1E3A8A] to-[#2D1B69]',
  },
  {
    icon: 'ri-mail-fill',
    label: 'Email Us',
    value: 'info@creativeinstitutellc.com',
    sub: 'We respond to every message',
    href: 'mailto:info@creativeinstitutellc.com',
    accent: 'from-[#5B21B6] to-[#7C3AED]',
  },
  {
    icon: 'ri-map-pin-fill',
    label: 'Service Area',
    value: 'Fayetteville, NC & Surrounding Areas',
    sub: 'Mobile-based · We come to you',
    href: 'tel:9102424567',
    accent: 'from-[#D97706] to-[#F59E0B]',
  },
];

export default function ContactSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: cardsRef, isVisible: cardsVis } = useScrollAnimation({ threshold: 0.05 });
  const { ref: shareRef, isVisible: shareVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: ctaRef, isVisible: ctaVis } = useScrollAnimation({ threshold: 0.2 });
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('https://creativeinstitutellc.com').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareUrl = encodeURIComponent('https://creativeinstitutellc.com');
  const shareText = encodeURIComponent('Check out Creative Institute — an amazing creative arts program in Fayetteville, NC! Where Every Creative Mind Matters.');

  return (
    <section className="py-28 bg-gradient-to-br from-[#F8FAFF] via-white to-[#FFFBF2] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-purple/6 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest border border-brand-yellow/30 mb-4`}>
            Get In Touch
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-brand-blue-dark mb-4 leading-tight`}>
            We&apos;d Love to<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED]">Hear From You</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-xl mx-auto leading-relaxed`}>
            Whether you have questions, want to enroll, or just want to learn more — reach out. We respond to every message.
          </p>
        </div>

        {/* Contact Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {contactCards.map((card, i) => (
            <a
              key={card.label}
              href={card.href}
              target={card.href.startsWith('http') ? '_blank' : undefined}
              rel={card.href.startsWith('http') ? 'nofollow noreferrer' : undefined}
              className={`scroll-hidden ${cardsVis ? 'scroll-visible' : ''} group rounded-3xl overflow-hidden card-lift cursor-pointer block`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className={`bg-gradient-to-br ${card.accent} p-7`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/20 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${card.icon} text-white text-xl`} />
                </div>
                <p className="font-body text-white/70 text-xs uppercase tracking-widest font-semibold mb-1">{card.label}</p>
                <p className="font-heading text-white font-bold text-base leading-snug break-all">{card.value}</p>
              </div>
              <div className="bg-white px-7 py-4 flex items-center justify-between border-t border-gray-100">
                <p className="font-body text-gray-400 text-xs">{card.sub}</p>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-gray-300 group-hover:text-brand-purple group-hover:translate-x-1 transition-all duration-300" />
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Share Strip */}
        <div ref={shareRef} className={`scroll-hidden ${shareVis ? 'scroll-visible' : ''} mb-8 rounded-2xl border border-gray-100 bg-white px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-5`}>
          <div>
            <p className="font-heading text-brand-blue-dark font-bold text-base mb-0.5">Know someone who&apos;d love this?</p>
            <p className="font-body text-gray-400 text-sm">Share Creative Institute with a friend or family member.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-end">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/20 text-[#1877F2] font-body text-xs font-semibold hover:bg-[#1877F2]/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-facebook-fill text-sm" /> Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 text-gray-700 font-body text-xs font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-twitter-x-line text-sm" /> Share on X
            </a>
            <a
              href={`https://www.instagram.com/creativeinstitutellc`}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-50 border border-pink-100 text-pink-600 font-body text-xs font-semibold hover:bg-pink-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-instagram-line text-sm" /> Instagram
            </a>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow-dark font-body text-xs font-semibold hover:bg-brand-yellow/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={copied ? 'ri-check-line text-sm' : 'ri-link text-sm'} />
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div ref={ctaRef} className={`scroll-hidden ${ctaVis ? 'scroll-visible' : ''} rounded-3xl bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] p-6 sm:p-10 flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8`}>
          <div>
            <h3 className="font-heading text-2xl lg:text-3xl font-black text-white mb-2">
              Not sure where to start?
            </h3>
            <p className="font-body text-white/70 text-sm leading-relaxed max-w-lg">
              Book a free consultation and we&apos;ll help you figure out exactly which program or service fits your goals — no pressure, just a conversation.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => {
                const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
                if (btn) btn.click();
              }}
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-all"
            >
              <i className="ri-calendar-check-line text-base" /> Schedule Free Consultation
            </button>
            <Link
              to="/contact"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/40 text-white font-body font-semibold text-sm cursor-pointer hover:border-brand-yellow hover:text-brand-yellow transition-all"
            >
              <i className="ri-message-3-line text-base" /> Send a Message
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
